import { useCallback } from 'react';
import { useRelayEnvironment, type GraphQLTaggedNode } from 'react-relay';
import { useApolloClient } from '@apollo/react-hooks';
import { type ReaderFragment, type IEnvironment, type ReaderSelection } from 'relay-runtime';

import { toDocumentNode } from './toDocumentNode';

export type UseReadFragmentInteropArgs = {
	id?: string;
	fragmentSpec: GraphQLTaggedNode;
};

export type UseReadFragmentInteropResult<TData> = (params?: { id?: string }) => TData | null;

export type UseReadFragmentInteropHook<TData> = (
	params: UseReadFragmentInteropArgs,
) => UseReadFragmentInteropResult<TData>;

function useReadFragmentInteropRelay<TData>(
	params: UseReadFragmentInteropArgs,
): UseReadFragmentInteropResult<TData> {
	const { fragmentSpec, id: defaultId } = params;
	const env = useRelayEnvironment();

	return useCallback(
		(callbackParams?: { id?: string }) => {
			const id = callbackParams?.id ?? defaultId;
			if (!id) {
				return null;
			}
			return readFragmentShim(env, fragmentSpec, id);
		},
		[env, fragmentSpec, defaultId],
	);
}

function useReadFragmentInteropApollo<TData>(
	params: UseReadFragmentInteropArgs,
): UseReadFragmentInteropResult<TData> {
	const { fragmentSpec: fragment, id: defaultId } = params;

	const client = useApolloClient();

	return useCallback(
		(callbackParams?: { id?: string }) => {
			const id = callbackParams?.id ?? defaultId;
			if (!id) {
				return null;
			}
			const data = client.readFragment<TData>({
				id,
				fragment: toDocumentNode(fragment),
			});

			return data as TData;
		},
		[client, fragment, defaultId],
	);
}

export function useReadFragmentInterop<TData>(
	params: UseReadFragmentInteropArgs,
): UseReadFragmentInteropResult<TData> {
	return process.env.BUILD_VARIANT === 'relay'
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useReadFragmentInteropRelay(params)
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			useReadFragmentInteropApollo(params);
}

function apolloFragmentIdToRelayFragmentId(apolloFragmentId: string) {
	return apolloFragmentId.replace(/^\$ROOT_QUERY\./, 'client:root:');
}

/**
 * Shim function that mimics Apollo's `readFragment` using Relay
 */
function readFragmentShim<TData>(
	environment: IEnvironment,
	fragment: GraphQLTaggedNode,
	fragmentId: string,
): TData | null {
	// Get the store from the environment
	const store = environment.getStore();

	// Get the source from the store
	const source = store.getSource();

	// Get the record using the fragmentId
	const rootRecord = source.get(apolloFragmentIdToRelayFragmentId(fragmentId));

	if (!rootRecord) {
		return null;
	}

	// Define a function to read the fields specified in the fragment
	const readFields = (recordId: string, fields: readonly ReaderSelection[]): any => {
		const record = source.get(recordId);
		if (!record) {
			return null;
		}

		const data = {} as any;
		for (const field of fields) {
			if (field.kind === 'LinkedField') {
				// For linked fields, we need to get the reference and recursively read
				const linkedRecordRef = record[field.name];
				if (linkedRecordRef && typeof linkedRecordRef === 'object' && '__ref' in linkedRecordRef) {
					const linkedRecordId = (linkedRecordRef as { __ref: string }).__ref;
					data[field.name] = readFields(linkedRecordId, field.selections);
				} else {
					data[field.name] = null;
				}
			} else if (field.kind === 'ScalarField') {
				data[field.name] = record[field.name];
			} else {
				throw new Error('Unsupported field');
			}
		}
		return data;
	};

	const readerFragment = fragment as ReaderFragment;

	const topLevelFields = readerFragment?.selections;

	return readFields(apolloFragmentIdToRelayFragmentId(fragmentId), topLevelFields ?? []);
}
